import React, { useState, useEffect } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import map from 'lodash/map';

import { chartColors, defaultBoostThreshold } from 'helpers/constants';
import { ChartNoDataText } from 'sharedComponents/ChartComponents';
import { translate } from 'helpers/translateHelper';
import colors from 'theme/_colors.scss';
import FFTTooltip from './Tooltip/FFTTooltip';
import { getMeasurementUnitSymbol } from 'helpers/unitHelper';
import { chartHeightConstants, default3DOptions, PiezoSensor } from './constants';
import { determineVisibility, getSignalColor, getSignalName, shouldProcessItem } from './helpers';
import { formatDateTime, normalizeUtcTimestamp } from 'helpers/dateTimeFormatter';

const chartConfig = {
    title: {
        text: null
    },
    chart: {
        type: 'area',
        zoomType: null,
        events: {},
        animation: false
    },
    colors: chartColors,
    boost: {
        enabled: false,
    },
    time: {
        useUTC: false
    },
    xAxis: [
        {
            id: 'x1',
            crosshair: {
                enabled: true,
                width: 2,
                color: colors.tundora,
            },
            labels: {
                style: {
                    color: colors.black,
                }
            },
        },
        {
            id: 'x2',
            labels: {
                style: {
                    color: colors.black
                }
            },
        }
    ],
    yAxis: {
        labels: {
            style: {
                color: colors.black
            }
        },
    },
    legend: {
        // Added custom legends
        enabled: false,
    },
    credits: {
        enabled: false,
    },
    tooltip: {
        shared: true,
        valueDecimals: 2,
        useHTML: true,
        backgroundColor: 'rgba(255, 255, 255, 0.6)',
        shadow: false,
        borderWidth: 0,
        dateTimeLabelFormats: {
            day: '%d.%m.%Y<br/>%H:%M',
            hour: '%d.%m %H:%M',
        },
    },
    plotOptions: {
        series: {
            turboThreshold: 0,
            animation: false,
            boostThreshold: 0,
        },
        area: {
            depth: 80,
            marker: {
                enabled: false
            },
            states: {
                inactive: {
                    enabled: false
                }
            }
        },
    },
    lang: {
        noData: 'No data'
    },
    noData: {
        useHTML: true
    },
    exporting: {
        enabled: false,
        buttons: {
            contextButton: {
                menuItems: ['downloadPNG'],
                verticalAlign: 'bottom',
                x: 10,
                y: 0
            }
        }
    }
};

const getFormattedData = (fftDataArray = [], spectrumRangeSelection, selectedLegends) => {
    const formattedData = [];
    let seriesLength = 0;

    // Count timestamps first
    const timestamps = fftDataArray.map(fftData => fftData?.timestamp).filter(Boolean);
    const timestampCount = timestamps.length;

    fftDataArray.forEach((fftData, fftDataIndex) => {
        const {
            dataValues: data,
            magnitudeUnitSymbol,
            frequencyUnitSymbol
        } = fftData || {};

        const timestamp = fftData?.timestamp;
        const timestampLegends = selectedLegends[timestamp] || {};

        formattedData.push({
            name: formatDateTime(normalizeUtcTimestamp(timestamp)),
            showInlegend: false,
            enableMouseTracking: false,
            custom: {
                isTimestamp: true,
            },
            id: timestamp,
            data: [],
            color: 'transparent',
            visible: timestampLegends?.visible !== false,
            marker: {
                enabled: false
            }
        });

        if (data) {
            let signalCount = 0;
            data?.forEach((item) => {
                if (shouldProcessItem(spectrumRangeSelection, item)) {
                    const seriesData = map(item.sensorAxisDataValues, (dataValues) => [dataValues.frequency, dataValues.magnitude]);
                    seriesLength += seriesData.length;

                    const signalName = getSignalName(item.sensorAxisName);
                    const visible = determineVisibility(timestampLegends, signalName, timestampCount, signalCount);
                    const color = getSignalColor(item.sensorAxisName, fftDataIndex);

                    formattedData.push({
                        name: signalName,
                        data: seriesData,
                        lineWidth: 1,
                        id: `fft-${timestamp}-${item.sensorAxisName}`,
                        tooltip: {
                            headerFormat: '',
                            pointFormat: ` <b>${frequencyUnitSymbol}:</b> {point.x} <br /><b>${magnitudeUnitSymbol}:</b> {point.y}`
                        },
                        connectNulls: true,
                        color,
                        timestamp,
                        visible,
                        showInlegend: false
                    });
                    signalCount++;
                }
            });
        }
    });
    return { formattedData, seriesLength };
};


const useChartConfig = (fftData, selectedSpectrumRange, selectedLegends, selectedTimestamps, containerRef) => {
    const [options, setOptions] = useState({});

    useEffect(() => {
        const containerHeight = containerRef?.current?.clientHeight || chartHeightConstants.default;
        chartConfig.chart.height = Math.max(chartHeightConstants.min, containerHeight - chartHeightConstants.reserved);
    }, []);

    useEffect(() => {
        const { formattedData, seriesLength } = getFormattedData(fftData, selectedSpectrumRange, selectedLegends) || {};
        chartConfig.series = formattedData;
        const style = {
            fontSize: '12px',
            fontFamily: 'ABBvoice-Regular',
            color: colors.black
        };
        chartConfig.plotOptions.series.boostThreshold = seriesLength > defaultBoostThreshold ? 1 : 0;
        chartConfig.xAxis[0].title = {
            text: `${translate('ABB.Powertrain.Frontend.FFTxAxisText')} [${fftData?.[0]?.frequencyUnitSymbol}]`,
            style
        };
        chartConfig.xAxis[1].title = {
            text: `${translate('ABB.Powertrain.Frontend.opFFTRevolutionsPerMinuteText')} [${getMeasurementUnitSymbol(14).toUpperCase()}]`,
            style
        };
        chartConfig.yAxis.title = {
            text: `${translate('ABB.Powertrain.Frontend.FFTyAxisText')} [${fftData?.[0]?.magnitudeUnitSymbol}]`,
            margin: 50,
            style
        };
        chartConfig.lang.noData = renderToStaticMarkup(
            <ChartNoDataText
                title={translate('ABB.Powertrain.Frontend.chartNoMeasurementData')}
                text={translate('ABB.Powertrain.Frontend.chartNoMeasurementDataUserGuide')} />);
        chartConfig.enableNoDataZoom = true;
        chartConfig.tooltip.formatter = function () {
            return renderToStaticMarkup(
                <FFTTooltip
                    points={this.points}
                    frequencyUnitSymbol={fftData?.[0]?.frequencyUnitSymbol}
                    magnitudeUnitSymbol={fftData?.[0]?.magnitudeUnitSymbol}
                    precision={fftData?.[0]?.precision}
                />
            );
        };
        // Set options3d based on selectedTimestamps length
        const timestampCount = selectedTimestamps?.length || 1;
        let options3d;
        if (selectedSpectrumRange === PiezoSensor.id) {
            // For PiezoSensor, limit to max 3
            options3d = default3DOptions[Math.min(Math.max(timestampCount, 1), 3)] || default3DOptions[1];
            chartConfig.plotOptions.area.depth = 60;
        } else {
            options3d = default3DOptions[Math.min(timestampCount, 6)] || default3DOptions[1];
            chartConfig.plotOptions.area.depth = options3d.areaDepth;
        }
        chartConfig.chart.options3d = options3d;

        setOptions({ ...chartConfig });
    }, [fftData, selectedSpectrumRange, selectedTimestamps, selectedLegends]);

    return options;
};

export default useChartConfig;
